import React, { useState } from "react";
import { FaShoppingBag, FaBoxOpen } from "react-icons/fa";
import { buildAssetUrl } from "../../../utils/api";
import useStore from "../../../store/useStore";

const extractImageCandidate = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) {
    return val.map(extractImageCandidate).find(Boolean) || null;
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return extractImageCandidate(parsed[0]);
        }
      } catch (e) {}
    }
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        return extractImageCandidate(JSON.parse(trimmed));
      } catch (e) {}
    }
    return trimmed;
  }
  if (typeof val === "object") {
    return (
      val.url ||
      val.image_url ||
      val.product_image_url ||
      val.image_path ||
      val.path ||
      val.src ||
      val.image ||
      val.product_image ||
      val.file_path ||
      val.filename ||
      null
    );
  }
  return null;
};

const OrderItemThumb = ({ src, fallbackSrc, alt }) => {
  const [hasError, setHasError] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);

  const primaryUrl = src ? buildAssetUrl(src) : null;
  const secondaryUrl = fallbackSrc ? buildAssetUrl(fallbackSrc) : null;

  let activeUrl = null;
  if (!hasError && primaryUrl) {
    activeUrl = primaryUrl;
  } else if (!triedFallback && secondaryUrl && secondaryUrl !== primaryUrl) {
    activeUrl = secondaryUrl;
  }

  return (
    <div className="relative h-14 w-14 sm:h-16 sm:w-16 shrink-0 overflow-hidden rounded-xl bg-gray-50 border border-gray-100 p-1 flex items-center justify-center">
      {activeUrl ? (
        <img
          src={activeUrl}
          alt={alt || "Product"}
          className="h-full w-full object-contain"
          onError={() => {
            if (!triedFallback && secondaryUrl && secondaryUrl !== primaryUrl) {
              setTriedFallback(true);
            } else {
              setHasError(true);
            }
          }}
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-lg bg-emerald-50/70 text-[#046a38]">
          <FaShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 opacity-70" />
        </div>
      )}
    </div>
  );
};

const OrderConfirmedItems = ({ items = [], order = null, onViewDetails }) => {
  const products = useStore((state) => state.products);
  const cart = useStore((state) => state.cart) || [];
  const cart_meta = useStore((state) => state.cart_meta);

  const rawList = (items && items.length > 0)
    ? items
    : (order?.items || order?.order_items || []);

  // Dynamic delivery options lookup from API
  const deliveryOptions = cart_meta?.delivery_options || [];
  const rawDeliveryType = String(order?.delivery_type || "").toUpperCase();

  const matchedDeliveryOpt = deliveryOptions.find(
    (o) => String(o.type).toUpperCase() === rawDeliveryType
  );

  let dynamicDeliveryFee = parseFloat(order?.delivery_fee ?? 0);
  if (matchedDeliveryOpt && matchedDeliveryOpt.fee != null) {
    dynamicDeliveryFee = Number(matchedDeliveryOpt.fee);
  }

  const slotText =
    order?.delivery_slot_name ||
    order?.slot_name ||
    (order?.notes?.startsWith("Time slot:")
      ? order.notes.replace("Time slot:", "").trim()
      : "");

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center gap-2">
          <FaShoppingBag className="h-5 w-5 text-[#046a38]" />
          <h2 className="text-base font-bold text-gray-900">
            Order Items ({rawList.length})
          </h2>
        </div>
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="text-xs font-semibold text-[#046a38] hover:underline"
          >
            View Details &rarr;
          </button>
        )}
      </div>

      {/* Dynamic Delivery Option Summary Bar */}
      {rawDeliveryType && (
        <div className="mb-3.5 flex items-center justify-between rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-500">Delivery:</span>
            <span
              className={`font-bold px-2 py-0.5 rounded text-[11px] border ${
                rawDeliveryType === "SCHEDULED"
                  ? "bg-emerald-50 text-[#046a38] border-emerald-200"
                  : rawDeliveryType === "EXPRESS"
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-blue-50 text-blue-800 border-blue-200"
              }`}
            >
              {rawDeliveryType}
            </span>
            {slotText && (
              <span className="font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 text-[10px]">
                {slotText}
              </span>
            )}
          </div>
          {dynamicDeliveryFee > 0 && (
            <span className="font-bold text-xs text-gray-900">
              Delivery: ₹{dynamicDeliveryFee.toFixed(2)}
            </span>
          )}
        </div>
      )}

      {rawList.length === 0 ? (
        <div className="py-8 text-center text-gray-400">
          <FaBoxOpen className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-xs sm:text-sm font-medium">No item details available</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto pr-1">
          {rawList.map((item, idx) => {
            const productId = item.product_id ?? item.id;
            const matchedProduct = products.find(
              (p) => String(p.product_id ?? p.id) === String(productId)
            );
            const matchedCartItem = cart.find(
              (c) => String(c.product_id ?? c.id) === String(productId)
            );

            const name =
              item.product_name ||
              item.name ||
              item.product?.name ||
              matchedProduct?.name ||
              "Product";

            // Build variant label: combine variant_name (e.g. "500") + unit_name (e.g. "gm") → "500 gm"
            const resolveVariant = () => {
              const uName = (
                item.unit_name ||
                item.variant_unit ||
                item.unit ||
                item.product?.unit_name ||
                item.product?.unit ||
                matchedProduct?.unit_name ||
                matchedProduct?.unit ||
                matchedCartItem?.unit_name ||
                matchedCartItem?.unit ||
                (matchedProduct?.variants && matchedProduct.variants[0]
                  ? (matchedProduct.variants[0].unit_name || matchedProduct.variants[0].unit || '')
                  : '') ||
                ''
              ).toString().trim();

              const rawV = (
                typeof item.variant === 'string'
                  ? item.variant
                  : typeof item.variant === 'number'
                  ? String(item.variant)
                  : item.variant_name ||
                    item.variant?.variant_name ||
                    item.variant?.name ||
                    item.product?.variant_name ||
                    matchedProduct?.variant_name ||
                    matchedCartItem?.variant_name ||
                    matchedCartItem?.variant ||
                    ''
              ).toString().trim();

              if (!rawV && !uName) return '';
              if (rawV && !uName) return rawV;
              if (!rawV && uName) return uName;

              // If rawV already contains letters/unit (e.g. "500 gm", "1 kg")
              if (/[a-zA-Z]/.test(rawV)) return rawV;

              // Otherwise rawV is numeric (e.g. "500", "1") -> append unit: "500 gm"
              return `${rawV} ${uName}`.trim();
            };
            const variant = resolveVariant();

            const qty = Math.max(1, parseInt(item.quantity ?? item.qty ?? 1, 10));

            const rawLineTotal = parseFloat(
              item.line_total ??
              item.total ??
              item.total_amount ??
              item.item_total ??
              0
            );

            const rawUnitPrice = parseFloat(
              item.unit_price ??
              item.price ??
              item.selling_price ??
              item.discount_price ??
              item.variant_price ??
              0
            );

            const itemTotal =
              rawLineTotal > 0
                ? rawLineTotal
                : rawUnitPrice > 0
                ? rawUnitPrice * qty
                : matchedProduct?.price
                ? parseFloat(matchedProduct.price) * qty
                : 0;

            const unitPrice =
              itemTotal > 0 && qty > 0
                ? itemTotal / qty
                : rawUnitPrice > 0
                ? rawUnitPrice
                : matchedProduct?.price
                ? parseFloat(matchedProduct.price)
                : 0;

            const originalPrice = parseFloat(
              item.original_price ??
              item.originalPrice ??
              item.mrp ??
              item.product?.original_price ??
              matchedProduct?.originalPrice ??
              0
            );

            const hasDiscount = originalPrice > unitPrice && originalPrice > 0;
            const discountPercent = hasDiscount
              ? Math.round(((originalPrice - unitPrice) / originalPrice) * 100)
              : 0;

            const taxAmount = parseFloat(item.tax_amount ?? item.gst_amount ?? item.tax ?? 0);
            const gstRate = parseFloat(
              item.gst_rate ??
              item.gst_percent ??
              item.tax_rate ??
              item.gst ??
              item.gst_percentage ??
              0
            );

            const primaryImgRaw =
              extractImageCandidate(item.product_image) ||
              extractImageCandidate(item.image) ||
              extractImageCandidate(item.image1) ||
              extractImageCandidate(item.image2) ||
              extractImageCandidate(item.image_url) ||
              extractImageCandidate(item.thumbnail) ||
              extractImageCandidate(item.photo) ||
              extractImageCandidate(item.primary_image) ||
              extractImageCandidate(item.product?.image) ||
              extractImageCandidate(item.product?.image_url) ||
              extractImageCandidate(item.product?.image1) ||
              extractImageCandidate(item.product?.product_image) ||
              extractImageCandidate(item.product?.primary_image) ||
              extractImageCandidate(item.images) ||
              extractImageCandidate(item.gallery_images) ||
              extractImageCandidate(item.product?.images) ||
              extractImageCandidate(item.product?.gallery_images) ||
              extractImageCandidate(item.variant?.image) ||
              extractImageCandidate(item.variant_image);

            const fallbackImgRaw =
              extractImageCandidate(matchedProduct?.image) ||
              extractImageCandidate(matchedProduct?.image1) ||
              (Array.isArray(matchedProduct?.images) && matchedProduct.images.length > 0
                ? extractImageCandidate(matchedProduct.images[0])
                : null) ||
              extractImageCandidate(matchedProduct?.image2) ||
              extractImageCandidate(matchedProduct?.gallery_images) ||
              extractImageCandidate(matchedCartItem?.image) ||
              extractImageCandidate(matchedCartItem?.image1);

            return (
              <div
                key={item.id || item.order_item_id || item.product_id || idx}
                className="flex items-center justify-between gap-3 sm:gap-4 py-3 sm:py-3.5"
              >
                <div className="flex items-center gap-3 sm:gap-3.5 min-w-0 flex-1">
                  <OrderItemThumb
                    src={primaryImgRaw}
                    fallbackSrc={fallbackImgRaw}
                    alt={name}
                  />

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-xs sm:text-sm font-bold text-gray-900">
                      {name}
                    </h3>

                    {variant && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-semibold bg-emerald-50 text-[#046a38] border border-emerald-200/60 mt-0.5">
                        {variant}
                      </span>
                    )}

                    <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 mt-1">
                      <span>
                        Qty: <strong className="text-gray-900 font-semibold">{qty}</strong>
                      </span>
                      <span>&times;</span>
                      <span className="font-bold text-gray-800">
                        ₹{unitPrice.toFixed(2)}
                      </span>
                      {hasDiscount && (
                        <span className="line-through text-gray-400 text-[11px]">
                          ₹{originalPrice.toFixed(2)}
                        </span>
                      )}
                      {discountPercent > 0 && (
                        <span className="text-[10px] font-bold text-[#046a38] bg-emerald-50 px-1 py-0.5 rounded border border-emerald-100">
                          {discountPercent}% OFF
                        </span>
                      )}
                    </div>

                    {taxAmount > 0 ? (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        Incl. ₹{taxAmount.toFixed(2)} GST
                      </p>
                    ) : gstRate > 0 ? (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        Incl. {gstRate}% GST
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col items-end shrink-0 pl-2">
                  <span className="text-xs sm:text-sm md:text-base font-extrabold text-gray-900">
                    ₹{itemTotal.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">
                    Total
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderConfirmedItems;
